import React from 'react';
import PropTypes from 'prop-types';
import './ColorSelector.css';

/**
 * ColorSelector — Configurator
 *
 * Circular colour-swatch button with a tooltip that appears on hover.
 * Used in product-configurator colour pickers.
 *
 * Figma component set: 3812:1202
 * Variants: Status = Default | Hover | Selected & Hover | Selected | Error
 * `image` = the swatch as a photo (Figma 3812:1326 "asset" image fill — fabric
 * textures, the Crown Student cap colours); `color` stays under it as the
 * fallback while the photo loads.
 */

const ColorSelector = ({
  color = 'var(--color-gray-3)',
  image,
  name,
  selected = false,
  error = false,
  onClick,
  className = '',
  ...props
}) => {
  const stateClass = error
    ? 'cfg-color-selector--error'
    : selected
      ? 'cfg-color-selector--selected'
      : '';

  return (
    <button
      className={`cfg-color-selector ${stateClass} ${className}`.trim()}
      onClick={onClick}
      type="button"
      {...props}
    >
      <span className="cfg-color-selector__swatch">
        <span
          className={`cfg-color-selector__color${image ? ' cfg-color-selector__color--image' : ''}`}
          style={{ backgroundColor: color, backgroundImage: image ? `url(${image})` : undefined }}
        />
      </span>

      {name && (
        <span className="cfg-color-selector__tooltip typo-body-sm">
          {name}
        </span>
      )}
    </button>
  );
};

ColorSelector.propTypes = {
  /** CSS colour value for the swatch (hex, rgb, var(--…), etc.) */
  color: PropTypes.string,
  /** Photo swatch — covers the circle over `color` (the fallback while it loads) */
  image: PropTypes.string,
  /** Swatch name shown in the hover tooltip */
  name: PropTypes.string,
  /** Whether this swatch is currently selected */
  selected: PropTypes.bool,
  /** Whether this swatch is in an error state */
  error: PropTypes.bool,
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default ColorSelector;
